"use client";

import { useMemo, useState } from "react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import RailButton from "@/components/ui/RailButton";

export default function BaseWalletButton() {
  const { address, isConnected } = useAccount();

  const {
    connectAsync,
    connectors,
    isPending,
    pendingConnector,
  } = useConnect();

  const { disconnect } = useDisconnect();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Only show MetaMask + Coinbase
  const allowed = useMemo(() => {
    const allowNames = ["MetaMask", "Coinbase Wallet"];
    return connectors.filter((c) => allowNames.includes(c.name));
  }, [connectors]);

  if (isConnected && address) {
    return (
      <div className="w-full">
        <RailButton
          theme="neutral"
          onClick={() => disconnect()}
          hint="Disconnect wallet"
          badge="Connected"
        >
          {`Connected: ${address.slice(0, 6)}…${address.slice(-4)} (Disconnect)`}
        </RailButton>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <RailButton
        theme="base"
        loading={isPending}
        loadingText="Connecting…"
        onClick={() => {
          setError(null);
          setOpen((v) => !v);
        }}
        hint="Choose a wallet to connect"
        badge="Base / Coinbase"
      >
        Connect Wallet
      </RailButton>

      {open && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
          <div className="p-2 space-y-1">
            {allowed.map((c) => (
              <button
                key={c.uid}
                type="button"
                disabled={isPending}
                onClick={async () => {
                  setError(null);
                  setOpen(false);

                  try {
                    await connectAsync({ connector: c });
                  } catch (e) {
                    console.error("Wallet connect failed:", e);
                    setError(e?.message || e?.shortMessage || "Wallet connection failed");
                  }
                }}
                className={[
                  "w-full text-left px-3 py-2 rounded-lg text-sm",
                  "hover:bg-surfaceElevated transition",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                ].join(" ")}
              >
                {isPending && pendingConnector?.uid === c.uid
                  ? "Connecting…"
                  : c.name}
              </button>
            ))}

            {allowed.length === 0 && (
              <div className="px-3 py-2 text-xs text-textSecondary">
                No supported wallets found.
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="mt-2 text-xs text-red-400">❌ {error}</div>}
    </div>
  );
}
